import React, { useMemo, useState } from 'react';
import {
  Box, Card, CardActionArea, Typography, Grid, Chip, Avatar, Skeleton,
  AppBar, Toolbar, IconButton, Tooltip, Alert, alpha, Button, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
  Business as BusinessIcon,
  Logout as LogoutIcon,
  ArrowForward as ArrowForwardIcon,
  DomainDisabled as NoCustomersIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ThemeToggle } from '../../components/ui/theme-toggle';
import usePortalCustomers from './usePortalCustomers';
import { ProTableStats } from '../../components/pro-table';

const FS = '0.8125rem';
const TOOLBAR_SX = {
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiInputBase-input': { fontSize: FS },
};
const BTN_SX = { textTransform: 'none', fontSize: FS };

const readJson = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || '{}'); } catch (e) { return {}; }
};

const PortalCustomers = ({ onPortalGroupLogout }) => {
  const navigate = useNavigate();
  const { customers, loading, error, opening, openCustomer, enabling, enablePortal } = usePortalCustomers();
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [portalFilter, setPortalFilter] = useState('');

  const group = readJson('portalGroupData');
  const groupUser = readJson('portalGroupUser');

  const handleLogout = () => {
    if (onPortalGroupLogout) onPortalGroupLogout();
    navigate('/portal/login');
  };

  const handleConfirmEnable = async () => {
    const target = confirmTarget;
    setConfirmTarget(null);
    if (target) await enablePortal(target);
  };

  const isSelectable = (c) => c.portalEnabled && c.active;

  const visibleCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return customers.filter((c) => {
      if (portalFilter === 'ACTIVE' && !isSelectable(c)) return false;
      if (portalFilter === 'OFF' && isSelectable(c)) return false;
      if (!term) return true;
      return [c.name, c.trnNumber, c.emirate].some((v) => (v || '').toLowerCase().includes(term));
    });
  }, [customers, search, portalFilter]);

  const activeCount = customers.filter(isSelectable).length;

  const renderCard = (customer) => {
    const selectable = customer.portalEnabled && customer.active;
    const canEnable = customer.active && !customer.portalEnabled;
    const isOpening = opening === customer.id;
    const isEnabling = enabling === customer.id;

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={customer.id}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            transition: 'border-color .15s ease',
            '&:hover': selectable ? { borderColor: 'primary.main' } : {}
          }}
        >
          <CardActionArea
            disabled={!selectable || opening !== null}
            onClick={() => openCustomer(customer)}
            sx={{
              flexGrow: 1,
              opacity: selectable ? 1 : 0.6,
              p: 1.5,
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              flexDirection: 'column',
              display: 'flex',
              borderRadius: 1,
              '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%' }}>
              <Avatar
                variant="rounded"
                sx={{
                  width: 36, height: 36, fontWeight: 700, fontSize: '0.875rem',
                  bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.24 : 0.12),
                  color: 'primary.main'
                }}
              >
                {(customer.name || '?').charAt(0).toUpperCase()}
              </Avatar>

              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography sx={{ fontSize: FS, fontWeight: 600, lineHeight: 1.3 }} noWrap title={customer.name}>
                  {customer.name}
                </Typography>
                {customer.emirate && (
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {customer.emirate}
                  </Typography>
                )}
              </Box>

              <ArrowForwardIcon
                fontSize="small"
                sx={{ color: selectable ? 'primary.main' : 'text.disabled', mt: 0.5, flexShrink: 0 }}
              />
            </Box>

            {customer.trnNumber && (
              <Typography sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                TRN <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{customer.trnNumber}</Box>
              </Typography>
            )}

            <Box sx={{ mt: 'auto', pt: 1.25, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                size="small"
                label={selectable ? 'Portal active' : (canEnable ? 'Portal not enabled' : 'Portal unavailable')}
                color={selectable ? 'success' : 'default'}
                sx={{ fontSize: '0.6875rem', height: 22 }}
              />
              {customer.type && (
                <Chip
                  size="small"
                  label={customer.type.replace(/_/g, ' ').toLowerCase()}
                  sx={{ fontSize: '0.6875rem', height: 22, textTransform: 'capitalize' }}
                />
              )}
              {isOpening && (
                <Typography sx={{ fontSize: '0.75rem', color: 'primary.main', ml: 'auto', fontWeight: 600 }}>
                  Opening...
                </Typography>
              )}
            </Box>
          </CardActionArea>
          {canEnable && (
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <Button
                size="small"
                variant="contained"
                sx={BTN_SX}
                disabled={isEnabling || opening !== null}
                onClick={() => setConfirmTarget(customer)}
              >
                {isEnabling ? 'Enabling...' : 'Enable portal'}
              </Button>
            </Box>
          )}
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
      >
        <Toolbar sx={{ gap: 1.5 }}>
          <Avatar
            variant="rounded"
            sx={{
              width: 36, height: 36,
              bgcolor: (t) => alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.24 : 0.12),
              color: 'primary.main'
            }}
          >
            <BusinessIcon fontSize="small" />
          </Avatar>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }} noWrap>
              {group?.name || 'Customer Group'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>
              {groupUser?.fullName || groupUser?.username || ''}
            </Typography>
          </Box>

          <ThemeToggle />
          <Tooltip title="Logout" arrow>
            <IconButton size="small" onClick={handleLogout} sx={{ color: 'text.secondary' }}>
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ px: 3, py: 2.5 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>Select a customer</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25, mb: 2 }}>
          Choose the customer portal you want to open.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && customers.length > 0 && (
          <>
            <ProTableStats
              cards={[
                { key: '', label: 'Customers', value: customers.length },
                { key: 'ACTIVE', label: 'Portal active', value: activeCount, color: 'success.main' },
                { key: 'OFF', label: 'Not enabled', value: customers.length - activeCount, color: 'text.secondary' },
              ].map((c) => ({ ...c, highlight: portalFilter === c.key, onClick: () => setPortalFilter(c.key) }))}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, ...TOOLBAR_SX }}>
              <TextField
                size="small"
                placeholder="Search by name, TRN or emirate..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: '1 1 200px', maxWidth: 420 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  )
                }}
              />
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 'auto', whiteSpace: 'nowrap' }}>
                {visibleCustomers.length} customers
              </Typography>
            </Box>
          </>
        )}

        {loading ? (
          <Grid container spacing={1.5}>
            {[0, 1, 2, 3].map((i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Card elevation={0} sx={{ borderRadius: 1, border: 1, borderColor: 'divider', p: 1.5, bgcolor: 'background.paper' }}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Skeleton variant="rounded" width={36} height={36} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="70%" height={20} />
                      <Skeleton variant="text" width="40%" height={14} />
                    </Box>
                  </Box>
                  <Skeleton variant="rounded" width={96} height={22} sx={{ mt: 1.5 }} />
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : customers.length === 0 ? (
          <Card
            elevation={0}
            sx={{ borderRadius: 1, border: 1, borderStyle: 'dashed', borderColor: 'divider', bgcolor: 'background.paper', py: 5, px: 3, textAlign: 'center' }}
          >
            <NoCustomersIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
              No customers assigned
            </Typography>
            <Typography sx={{ fontSize: FS, color: 'text.secondary', mt: 0.5 }}>
              No customers have been assigned to this group yet. Please contact your typing centre.
            </Typography>
          </Card>
        ) : visibleCustomers.length === 0 ? (
          <Typography sx={{ fontSize: FS, color: 'text.secondary', textAlign: 'center', py: 4 }}>
            No matching customers
          </Typography>
        ) : (
          <Grid container spacing={1.5}>
            {visibleCustomers.map(renderCard)}
          </Grid>
        )}
      </Box>

      <Dialog open={!!confirmTarget} onClose={() => setConfirmTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>Enable portal?</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ fontSize: FS }}>
            This turns on the customer portal for {confirmTarget?.name}. You will then be able to open it from here.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setConfirmTarget(null)} sx={BTN_SX}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmEnable} sx={BTN_SX}>Enable portal</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalCustomers;
